<template>
  <div class="login-container">
    <div class="login-card">
      <!-- 头部 -->
      <div class="login-header">
        <div class="logo">🎵</div>
        <h1 class="title">TikTok 登录</h1>
        <p class="subtitle">使用您的 TikTok 账号登录</p>
      </div>

      <!-- 登录内容 -->
      <div class="login-content">
        <!-- 错误信息 -->
        <div v-if="getters.hasError.value" class="error-message">
          <div class="error-icon">⚠️</div>
          <div class="error-text">
            <strong>登录失败</strong>
            <p>{{ state.error }}</p>
          </div>
          <button @click="actions.clearError" class="error-close">✕</button>
        </div>

        <!-- 登录说明 -->
        <div class="login-info">
          <h3>登录流程</h3>
          <ol class="steps">
            <li>点击下方"使用 TikTok 登录"按钮</li>
            <li>跳转到 TikTok 官方授权页面</li>
            <li>使用您的 TikTok 账号登录并授权</li>
            <li>自动返回并完成登录</li>
          </ol>
        </div>

        <!-- 登录按钮 -->
        <div class="login-actions">
          <button 
            @click="handleLogin" 
            :disabled="state.loading"
            class="login-btn"
          >
            <span v-if="state.loading" class="loading-spinner">⏳</span>
            <span v-else class="tiktok-icon">🎵</span>
            {{ state.loading ? '正在跳转...' : '使用 TikTok 登录' }}
          </button>
        </div>

        <!-- 注意事项 -->
        <div class="notice">
          <h4>注意事项</h4>
          <ul>
            <li>本应用仅用于演示 TikTok OAuth 登录流程</li>
            <li>我们只会获取您的基本公开信息</li>
            <li>您可以随时撤销授权</li>
            <li>您的登录信息将安全存储在本地</li>
          </ul>
        </div>
      </div>

      <!-- 底部 -->
      <div class="login-footer">
        <router-link to="/" class="back-link">
          ← 返回首页
        </router-link>
      </div>
    </div>

    <!-- 背景装饰 -->
    <div class="bg-decoration">
      <div class="circle circle-1"></div>
      <div class="circle circle-2"></div>
      <div class="circle circle-3"></div>
    </div>
  </div>
</template>

<script>
import { onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '../store'

export default {
  name: 'Login',
  setup() {
    const router = useRouter()
    const route = useRoute()
    const { state, getters, actions } = useAuthStore()

    // 处理登录
    const handleLogin = async () => {
      try {
        await actions.initiateLogin()
      } catch (error) {
        console.error('登录失败:', error)
      }
    }

    // 初始化
    onMounted(async () => {
      // 如果已经登录，重定向到目标页面或首页
      if (getters.isAuthenticated.value) {
        const redirect = route.query.redirect || '/'
        router.push(redirect)
        return
      }

      // 检查服务状态
      await actions.checkHealth()
    })

    return {
      state,
      getters,
      actions,
      handleLogin
    }
  }
}
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 1rem;
  position: relative;
  overflow: hidden;
}

.login-card {
  background: white;
  border-radius: 20px;
  box-shadow: 0 30px 60px rgba(0, 0, 0, 0.2);
  max-width: 500px;
  width: 100%;
  overflow: hidden;
  position: relative;
  z-index: 2;
}

.login-header {
  text-align: center;
  padding: 2rem 2rem 1rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.logo {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.title {
  font-size: 2rem;
  margin-bottom: 0.5rem;
  font-weight: 700;
}

.subtitle {
  opacity: 0.9;
  font-size: 1rem;
}

.login-content {
  padding: 2rem;
}

.error-message {
  display: flex;
  align-items: flex-start;
  background: #ffebee;
  border: 1px solid #ffcdd2;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  position: relative;
}

.error-icon {
  font-size: 1.2rem;
  margin-right: 0.75rem;
  flex-shrink: 0;
}

.error-text {
  flex: 1;
}

.error-text strong {
  color: #d32f2f;
  display: block;
  margin-bottom: 0.25rem;
}

.error-text p {
  color: #666;
  margin: 0;
  font-size: 0.9rem;
}

.error-close {
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 0;
  font-size: 1rem;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-info {
  margin-bottom: 2rem;
}

.login-info h3 {
  color: #333;
  margin-bottom: 1rem;
  font-size: 1.2rem;
}

.steps {
  color: #666;
  line-height: 1.6;
  padding-left: 1rem;
}

.steps li {
  margin-bottom: 0.5rem;
}

.login-actions {
  margin-bottom: 2rem;
}

.login-btn {
  width: 100%;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
}

.login-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
}

.login-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.loading-spinner {
  font-size: 1.2rem;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.tiktok-icon {
  font-size: 1.3rem;
}

.notice {
  background: #f5f5f5;
  border-radius: 8px;
  padding: 1.5rem;
}

.notice h4 {
  color: #333;
  margin-bottom: 1rem;
  font-size: 1rem;
}

.notice ul {
  color: #666;
  font-size: 0.9rem;
  line-height: 1.5;
  padding-left: 1rem;
}

.notice li {
  margin-bottom: 0.5rem;
}

.login-footer {
  padding: 1.5rem 2rem;
  border-top: 1px solid #eee;
  text-align: center;
}

.back-link {
  color: #667eea;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s ease;
}

.back-link:hover {
  color: #5a6fd8;
}

/* 背景装饰 */
.bg-decoration {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
}

.circle {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  animation: float 6s ease-in-out infinite;
}

.circle-1 {
  width: 200px;
  height: 200px;
  top: 10%;
  left: 10%;
  animation-delay: 0s;
}

.circle-2 {
  width: 150px;
  height: 150px;
  top: 60%;
  right: 15%;
  animation-delay: 2s;
}

.circle-3 {
  width: 100px;
  height: 100px;
  bottom: 20%;
  left: 20%;
  animation-delay: 4s;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0) rotate(0deg);
  }
  50% {
    transform: translateY(-20px) rotate(180deg);
  }
}

/* 响应式设计 */
@media (max-width: 768px) {
  .login-card {
    margin: 1rem;
  }
  
  .login-header {
    padding: 1.5rem 1.5rem 1rem;
  }
  
  .login-content {
    padding: 1.5rem;
  }
  
  .title {
    font-size: 1.75rem;
  }
  
  .login-btn {
    padding: 0.875rem 1.5rem;
    font-size: 1rem;
  }
  
  .circle {
    display: none;
  }
}
</style> 